import React, { useEffect } from "react"
import AppCardLoader from "core/components/AppCardLoader"
import {
  selectMachinesOverview,
  selectSiteMachines,
} from "store/Machines/machinesSlice"
import { getStatusColor } from "core/helpers"
import { MachineCollection } from "models/organization"
import { RootState } from "store/store"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

/* eslint-disable @typescript-eslint/no-empty-function */
import { Grid, useTheme } from "@mui/material"

import BaseCard from "../BaseCard"
import MachineOverviewItem from "./subcomponents/MachineOverviewItem"
import { styles } from "./styles"
import { minutesToMilliseconds } from "date-fns"
import { useAppGetOrganizationQuery } from "core/hooks/apiHooks"
import { CORE_GROUP_ORGANIZATION_ID, renderFlags } from "core/constants/ui"

const MachinesOverview = () => {
  const { t } = useTranslation()
  const theme = useTheme()

  const selectState = (state: RootState) => state.global

  const { isUserLogged } = useSelector(selectState)

  const { organizationId } = useSelector(
    (state: RootState) => state.organizations,
  )

  const {
    organizationData,
    fulfilledTimeStamp: timestamp,
    isOrganizationFetching,
    isOrganizationError,
    refetch,
  } = useAppGetOrganizationQuery(organizationId, {
    skip: !isUserLogged && !organizationId,
    pollingInterval: minutesToMilliseconds(60),
  })

  const totalMachines: MachineCollection[] = useSelector((state: RootState) =>
    selectSiteMachines(state, { ...organizationData }),
  )
  const totalNumberOfMachines = totalMachines.length

  const machinesOverviewData = useSelector((state: RootState) =>
    selectMachinesOverview(state, totalMachines),
  )

  const hasDataAvailable =
    !!machinesOverviewData && !!Object.keys(machinesOverviewData).length

  const individualOverviewStatusItem = hasDataAvailable
    ? machinesOverviewData.overview.map((el: any, index: number) => ({
        ...el,
        color: getStatusColor(el.key, theme).main,
        selected: index === 1,
      }))
    : []

  const totalMachinesOverviewStatusItem = hasDataAvailable
    ? machinesOverviewData.machines
    : []

  const overviewItemsArray = hasDataAvailable
    ? [totalMachinesOverviewStatusItem, ...individualOverviewStatusItem]
    : []

  const handleOnRetry = () => console.log("retry")

  const hasError = isOrganizationError || !hasDataAvailable

  useEffect(() => {
    if (CORE_GROUP_ORGANIZATION_ID !== organizationData?.organisation_id) return

    const intervalId = setInterval(() => {
      refetch()
      renderFlags.isFirstRenderDashboard = false
    }, 60 * 1000)

    return () => {
      clearInterval(intervalId)
    }
  }, [organizationData])

  return (
    <BaseCard
      lastUpdate={timestamp}
      sx={styles.card}
      title={t("machines.overview.text")}
    >
      <AppCardLoader
        isLoading={renderFlags.isFirstRenderDashboard && isOrganizationFetching}
        hasError={hasError}
        onRetry={handleOnRetry}
      >
        <Grid
          container
          justifyContent="space-between"
          spacing={1}
          sx={styles.gridContainer}
        >
          {overviewItemsArray.map(({ key: label, color, value }: any) => (
            <Grid key={label} item xs={6} md={2}>
              <MachineOverviewItem
                label={label}
                color={color}
                value={value}
                total={totalNumberOfMachines}
                showPercentage
              />
            </Grid>
          ))}
        </Grid>
      </AppCardLoader>
    </BaseCard>
  )
}

export default MachinesOverview
